<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    WordDocumentTemplatesService,
    type CustomerWordDocumentTemplate,
    type CustomerWordDocumentTemplateRequest,
    type CustomerUser as User,
    WordTemplateStatus
  } from '@/types/generated'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { Button } from '@/components/ui/button'
  import { Search } from 'lucide-vue-next'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { genericErrorToast } from '@/lib/toasts'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useTranslation } from '@/composables/useTranslation'

  const router = useRouter()
  const templates = ref<CustomerWordDocumentTemplate[]>([])
  const searchQuery = ref('')
  const selectedLawField = ref('')
  const showDeleteModal = ref(false)
  const templateToDelete = ref<CustomerWordDocumentTemplate | null>(null)
  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user as User)
  const lawFieldOptions = computed(() => useUserStore().lawFieldOptions)

  // Add translations
  const { t } = useTranslation({
    en: {
      createTemplate: 'Create Template',
      search: 'Search',
      lawField: 'Law Field',
      allLawFields: 'All Law Fields',
      name: 'Name',
      type: 'Type',
      status: 'Status',
      lawTopic: 'Law Topic',
      updated: 'Updated',
      firm: 'Firm',
      edit: 'Edit',
      delete: 'Delete',
      inReview: 'In Review',
      approved: 'Approved',
      draft: 'Draft',
      noTemplatesFound: 'No templates found.',
      deleteNotPossible: 'Delete Not Possible',
      cannotDeleteJupusTemplate: 'Jupus templates cannot be deleted.',
      close: 'Close',
      deleteTemplate: 'Delete Template',
      deleteTemplateConfirmation:
        'Are you sure you want to delete this template? This action cannot be undone.',
      cancel: 'Cancel',
      confirmDelete: 'Delete'
    },
    de: {
      createTemplate: 'Vorlage erstellen',
      search: 'Suchen',
      lawField: 'Rechtsgebiet',
      allLawFields: 'Alle Rechtsgebiete',
      name: 'Name',
      type: 'Typ',
      status: 'Status',
      lawTopic: 'Rechtsthema',
      updated: 'Aktualisiert',
      firm: 'Kanzlei',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      inReview: 'In Prüfung',
      approved: 'Freigegeben',
      draft: 'Entwurf',
      noTemplatesFound: 'Keine Vorlagen gefunden.',
      deleteNotPossible: 'Löschen nicht möglich',
      cannotDeleteJupusTemplate: 'Jupus-Vorlagen können nicht gelöscht werden.',
      close: 'Schließen',
      deleteTemplate: 'Vorlage löschen',
      deleteTemplateConfirmation:
        'Sind Sie sicher, dass Sie diese Vorlage löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirmDelete: 'Löschen'
    }
  })

  onMounted(async () => {
    const fetching = [fetchWordTemplates()]
    if (!user.value) {
      fetching.push(
        (async () => {
          await fetchUser()
        })()
      )
    }
    await Promise.all(fetching)
  })

  const fetchWordTemplates = async () => {
    templates.value = (await WordDocumentTemplatesService.wordDocumentTemplatesList()).results
  }

  const filteredTemplates = computed(() => {
    if (searchQuery.value === '' && selectedLawField.value === '') {
      return templates.value
    }
    return templates.value.filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
        (selectedLawField.value === '' || template.law_field === selectedLawField.value)
    )
  })

  const openDeleteModal = (template: CustomerWordDocumentTemplate) => {
    templateToDelete.value = template
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const confirmDelete = async () => {
    if (templateToDelete.value) {
      try {
        if (!templateToDelete.value.uuid) return
        await WordDocumentTemplatesService.wordDocumentTemplatesDestroy({
          uuid: templateToDelete.value.uuid
        })
        closeDeleteModal()
        await fetchWordTemplates()
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const createTemplate = async () => {
    try {
      const newTemplate = await WordDocumentTemplatesService.wordDocumentTemplatesCreate({
        formData: {
          name: 'Neue Vorlage',
          law_field: '',
          law_topic: '',
          description: '(leer)',
          system_prompt: '(leer)',
          is_jupus_template: false,
          replaceable_variables: null
        }
      })
      await router.push(
        ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
          ':uuid',
          newTemplate.uuid ? newTemplate.uuid : ''
        )
      )

      await router.push(
        ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
          ':uuid',
          newTemplate.uuid ? newTemplate.uuid : ''
        )
      )
    } catch (error) {
      console.error('Failed to create new template:', error)
      genericErrorToast()
    }
  }
</script>

<template>
  <SettingsPage v-if="templates && user">
    <template #actions>
      <button
        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
          text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
        @click="createTemplate"
      >
        <svg
          class="mr-2 h-4 w-4"
          fill="none"
          height="24"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line
            x1="12"
            x2="12"
            y1="5"
            y2="19"
          />
          <line
            x1="5"
            x2="19"
            y1="12"
            y2="12"
          />
        </svg>
        {{ t('createTemplate') }}
      </button>
    </template>
    <div class="space-y-4">
      <div class="flex items-center space-x-4">
        <div class="relative flex items-center">
          <Search class="ml-3 mr-2 h-4 w-4 text-gray-400" />
          <InputField
            v-model="searchQuery"
            name="search"
            :placeholder="t('search')"
          />
        </div>
        <div class="w-40 flex-shrink-0">
          <SelectField
            v-model="selectedLawField"
            :options="lawFieldOptions"
            name="rechtsgebiet"
            :title="t('lawField')"
            :select-option-text="t('lawField')"
            :allow-unselect="true"
            :unselected-option-text="t('allLawFields')"
          />
        </div>
      </div>

      <Table v-if="templates.length > 0">
        <TableHeader>
          <TableRow>
            <TableCell class="w-1/4 py-2 font-medium">{{ t('name') }}</TableCell>
            <TableCell class="w-1/8 py-2 font-medium">{{ t('type') }}</TableCell>
            <TableCell class="w-1/8 py-2 font-medium">{{ t('status') }}</TableCell>
            <TableCell class="w-1/8 py-2 font-medium">{{ t('lawField') }}</TableCell>
            <TableCell class="w-1/8 py-2 font-medium">{{ t('lawTopic') }}</TableCell>
            <TableCell class="w-1/8 py-2 font-medium">{{ t('updated') }}</TableCell>
            <TableCell class="w-12 py-2"></TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow
            v-for="template in filteredTemplates"
            :key="template.uuid"
            class="cursor-pointer hover:bg-gray-50"
            @click="
              router.push(
                ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
                  ':uuid',
                  template.uuid ? template.uuid : ''
                )
              )
            "
          >
            <TableCell class="py-4">{{ template.name }}</TableCell>
            <TableCell class="py-1">
              <span
                v-if="template.is_jupus_template"
                class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700
                  ring-1 ring-inset ring-purple-700/10"
              >
                Jupus
              </span>
              <span
                v-else
                class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1
                  ring-inset ring-red-700/10"
              >
                {{ t('firm') }}
              </span>
            </TableCell>
            <TableCell class="py-1">
              <span
                v-if="template.status === WordTemplateStatus.IN_REVIEW"
                class="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700
                  ring-1 ring-inset ring-yellow-700/10"
              >
                {{ t('inReview') }}
              </span>
              <span
                v-else-if="template.status === WordTemplateStatus.APPROVED"
                class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1
                  ring-inset ring-green-700/10"
              >
                {{ t('approved') }}
              </span>
              <span
                v-else
                class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1
                  ring-inset ring-gray-700/10"
              >
                {{ t('draft') }}
              </span>
            </TableCell>
            <TableCell class="py-1">{{ template.law_field || '-' }}</TableCell>
            <TableCell class="py-1">{{ template.law_topic || '-' }}</TableCell>
            <TableCell class="py-1">
              {{ new Date(template.updated_at).toLocaleDateString('de-DE') }}
            </TableCell>
            <TableCell
              class="py-1"
              @click.stop
            >
              <TableOptionsButton
                :options="[
                  {
                    label: t('edit'),
                    to: ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
                      ':uuid',
                      template.uuid ? template.uuid : ''
                    )
                  },
                  {
                    label: t('delete'),
                    onClick: () => openDeleteModal(template)
                  }
                ]"
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <div
        v-else
        class="py-4 text-center"
      >
        {{ t('noTemplatesFound') }}
      </div>
    </div>
  </SettingsPage>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent v-if="templateToDelete">
      <DialogHeader>
        <template v-if="templateToDelete?.is_jupus_template">
          <DialogTitle>{{ t('deleteNotPossible') }}</DialogTitle>
          <DialogDescription>{{ t('cannotDeleteJupusTemplate') }}</DialogDescription>
        </template>
        <template v-else>
          <DialogTitle>{{ t('deleteTemplate') }}</DialogTitle>
          <DialogDescription>
            {{ t('deleteTemplateConfirmation') }}
          </DialogDescription>
        </template>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="outline"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          v-if="!templateToDelete?.is_jupus_template"
          variant="destructive"
          @click="confirmDelete"
        >
          {{ t('confirmDelete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
